import {Avatar, List} from 'antd'
import {search_fetchAvailableOwners_Api} from 'apis'
import {getType} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import {createValue} from 'components/form/utils'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import {NavigationContext} from 'modules/navigation/NavigationContext'
import React, {useContext, useEffect, useRef} from 'react'
import {Link, Redirect} from 'react-router-dom'
import {paths} from 'views/MainPage/contains'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import {gSettingModalRoutes} from '../configs'
import {CustomKeys} from '../CustomKeys'
import renderClicker from '../functions/renderers/renderClicker'
import {bindQueryParams} from '../functions/routerHelper'
import AntdConfigWrapper from './AntdConfigWrapper'
import {useDebounceSearch} from './DebounceSearch'
import TooltipApp from "components/TooltipApp";
import {BiSolidChurch} from "react-icons/bi";
import getLinkToDetail from "../../../helpers/getLinkToDetail";
import {VerifiedSvg} from "../../../components/Svgs/VerifiedSvg";
import LoadingBlock from "../../Loading/LoadingBlock";

const OwnerItemWrapper = ({item, children, ...props}) => (
    <div
        className="py-2 cursor-pointer"
        {...props}>
        {children}
    </div>
)

const renderOwnerItem = ({actions, ...item}, index) => {
    const isEmpty = _.isEmpty(item)
    const verified = _.get(item, 'certified')

    return (
        <Translate key={index}>
            {(t) =>
                isEmpty ? (
                    <EmptyHolder/>
                ) : (
                    <div className="w-full py-2 flex items-center gap-2 custom-render-border relative">
                        <Avatar
                            size={40}
                            className="flex flex-center background-200 rounded-md"
                            src={getAvatar(item)}
                            icon={<BiSolidChurch size={20} className="text-color-500"/>}
                        />
                        <div className="flex flex-col flex-1">
                            <div className="flex items-center gap-3">
                                <Link to={getLinkToDetail(item)}
                                      className="font-bold text-color-000 leading-snug no-underline">
                                    {getTitle(item)}
                                </Link>
                                {verified === 1 && (
                                    <TooltipApp
                                        title={t('verified')}
                                        placement="right"
                                        color="#87d068">
                                        <VerifiedSvg
                                            className="ml-1 mb-1 lg:mb-2 zoom-animation"
                                        />
                                    </TooltipApp>
                                )}
                            </div>
                            <span className="font-medium text-xs text-blue-500 leading-snug italic">
                      {t(getType(item))}
                    </span>
                        </div>
                        {actions || (
                            <span style={{padding: '2px 10px'}}
                                  className="bg-primary-50 hover:bg-primary-100 rounded-md font-medium text-xs text-primary cursor-pointer">
                        {t('choose')}
                      </span>
                        )}
                    </div>
                )
            }
        </Translate>
    )
}

const OwnerModalContent = ({Wrapper = 'div', ...props}) => {
    const {setCurrent} = useContext(NavigationContext)

    const params = _.get(props, 'location.state')

    const [id, type] = [_.get(params, 'id'), _.get(params, 'type')]

    const [keyword, ___, searchInput] = useDebounceSearch({
        timeout: 500,
        className: 'w-full rounded-md px-2 py-1',
    })

    const apiInfo = search_fetchAvailableOwners_Api

    const onSelect = (value) => {
        setCurrent(value)
    }

    if (_.every([id, type], _.isEmpty)) {
        return <Redirect to={paths.homePath}/>
    }

    return (
        <Wrapper className="space-y-3 py-3">
            <div className="mb-3 font-bold text-center text-color-000 uppercase tracking-wide">
                <Translate>{(t) => t('edit owner')}</Translate>
            </div>
            {searchInput}
            <Async
                key={keyword}
                query={bindQueryParams([{id}, {type}])}
                values={{
                    keyword,
                }}
                apiInfo={apiInfo}>
                {({response, isLoading}) => {
                    if (Boolean(isLoading)) {
                        return <LoadingBlock/>
                    }
                    const items = Array.from(getResponseItems(response) || []).map(
                        ({owner}) => owner
                    )
                    return (
                        <List
                            loading={!!isLoading}
                            dataSource={items}
                            renderItem={renderClicker({
                                onClick: onSelect,
                                renderItem: renderOwnerItem,
                                Wrapper: OwnerItemWrapper,
                            })}
                            pagination={{
                                pageSize: 8,
                                hideOnSinglePage: true,
                            }}
                        />
                    )
                }}
            </Async>
        </Wrapper>
    )
}

export const EditOwnerModal = (props) => (
    <AntdConfigWrapper>
        <OwnerModalContent {...props} />
    </AntdConfigWrapper>
)

const OwnerPicker = ({id, type, name, defaultValue, onChange = NullAsDefault}) => {
    const t = useTranslate()
    const {
        current,
        history,
        setCurrent = NullAsDefault,
        handleGoBack = NullAsDefault,
    } = useContext(NavigationContext)

    // Capture the current value at mount time to detect stale selections from
    // a previous owner modal session (NavigationContext.current is not cleared
    // between page navigations, so it may already be set when this mounts).
    const staleCurrentRef = useRef(current)

    useEffect(() => {
        if (current) {
            if (current === staleCurrentRef.current) {
                // Stale value present before this component mounted — clear it
                // without triggering back-navigation to workspace.
                staleCurrentRef.current = null
                setCurrent(null)
                return
            }
            handleGoBack()
            onChange(createValue(name, current))
            setCurrent(null)
        }
    }, [name, current, onChange, setCurrent, handleGoBack])

    const editOwner = () => {
        history.push(
            gSettingModalRoutes(CustomKeys.edit_owner.name, {
                id,
                type,
            })
        )
    }

    return (
        renderOwnerItem({
            actions: (
                <span onClick={editOwner}
                      className="font-medium text-xs text-primary-500 hover:text-primary italic cursor-pointer">
            {t('edit')}
          </span>
            ),
            ...defaultValue,
        })
    )
}

export default OwnerPicker
